import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Chip,
    CircularProgress,
    Alert,
    Box,
} from "@mui/material";
import Axios from "axios";
import { getAuthHeader } from "../../utils/authToken";

const BASE = `${process.env.REACT_APP_URL}/api/v1/prep-programs`;

// Dashboard view of the teacher due-date digest (Phase 6 Stage 1) — renders the SAME stored
// JSON the teacher email was built from, so the two can't disagree.
const TeacherDigestModal = ({ programUuid, round, onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!round) return;
        setLoading(true);
        Axios.get(`${BASE}/${programUuid}/homework-rounds/${round.id}/teacher-digest`,
            { headers: { Authorization: getAuthHeader() } })
            .then((res) => setData(res.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [programUuid, round]);

    const wrongSummary = (q) =>
        Object.entries(q.wrong_answers || {})
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3)
            .map(([ans, n]) => `${ans || "(blank)"} ×${n}`)
            .join(", ");

    return (
        <Dialog open={!!round} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Homework results — teacher digest</DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box display="flex" justifyContent="center" py={3}><CircularProgress size={24} /></Box>
                ) : !data || !(data.digests || []).length ? (
                    <Alert severity="info">
                        No digest yet — it's built and emailed when the round comes due
                        (only for rounds with teacher notifications enabled).
                    </Alert>
                ) : (
                    (data.digests || []).map((d) => {
                        const dig = d.digest || {};
                        return (
                            <Box key={d.chapter_id} sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" fontWeight={600}>{d.chapter_name || `Chapter ${d.chapter_id}`}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                                    {dig.students_with_data ?? 0}/{dig.students_total ?? 0} students with practice in the window.
                                    {" "}
                                    {dig.set_scope === "COHORT"
                                        ? "Every student received the same questions."
                                        : "Students received different questions — counts are among those who attempted each."}
                                </Typography>
                                {(dig.questions || []).length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">No answered questions in the homework window.</Typography>
                                ) : (
                                    <Table size="small" sx={{ "& td, & th": { borderColor: "divider" } }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Q</TableCell>
                                                <TableCell>Question</TableCell>
                                                <TableCell>Missed</TableCell>
                                                <TableCell>Common wrong answers</TableCell>
                                                <TableCell>Missed by</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(dig.questions || []).map((q) => (
                                                <TableRow key={q.question_id}>
                                                    <TableCell>{q.order != null ? `#${q.order}` : ""}</TableCell>
                                                    <TableCell sx={{ maxWidth: 320 }}>
                                                        <Typography variant="body2" noWrap title={q.content_preview}>{q.content_preview}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip size="small"
                                                            color={q.attempted_by && q.wrong_by / q.attempted_by >= 0.5 ? "error" : "default"}
                                                            label={`${q.wrong_by}/${q.attempted_by}`} />
                                                        {q.revealed_count ? (
                                                            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                                                +{q.revealed_count} post-reveal
                                                            </Typography>
                                                        ) : null}
                                                    </TableCell>
                                                    <TableCell>{wrongSummary(q)}</TableCell>
                                                    <TableCell sx={{ maxWidth: 200 }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {(q.wrong_students || []).map((s) => s.name || s.student_id).join(", ")}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                                {(dig.no_data_students || []).length > 0 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                        No practice: {(dig.no_data_students || []).map((s) => s.name || s.student_id).join(", ")}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default TeacherDigestModal;
